import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchLikesByContentId,
  fetchPopularTools,
  fetchCommentsByContentId,
  postCommentAPI,
  fetchPickFromList,
  fetchDoMore,
  fetchVideoPickFromList,
  fetchLikesByContentIdAndSearch,
  fetchDiscoverData,
  fetchShareApi,
  fetchShareCount,
  fetchContentDetails
} from 'shared-ui/api'
import { CONTENT_TYPE, QUERY_KEY, ensureAiToolId } from 'shared-ui/utils/constants'
import { useNativeShare } from './useNativeShare'

export const useGetContentDetailsById = (params = {}, options = {}) => {
  const { id, isUGC, NotYetPublish } = params
  // Enhanced caching for AI tool content details with placeholder data
  return useQuery({
    queryKey: [QUERY_KEY.GET_CONTENT_DETAILS_BY_ID, id, isUGC, NotYetPublish],
    queryFn: async () => {
      const resp = await fetchContentDetails(id, isUGC, NotYetPublish)
      return resp
    },
    enabled: !!id,
    cacheTime: 0, // 10 minutes
    staleTime: 0,  // 5 minutes
    retry: 3,
    placeholderData: { id: 'placeholder', title: 'Loading...', description: '', image: '/placeholder-image.svg' },
    ...options
  })
}

export const useLikesByContentId = (content_id,isUGC, options = {}) => {
  const { page = 1, limit = 20, ...queryOptions } = options
  return useQuery({
    queryKey: ['LIKES_BY_CONTENT_ID', content_id, page, limit],
    queryFn: async () => {
      try {
        if (!content_id) throw new Error('Content ID is required')
        const resp = await fetchLikesByContentId(content_id, page, limit,isUGC)
       return resp
      } catch (error) {
        console.error('Error while fetching likes: ', error)
      }
    },
    enabled: !!content_id,
    ...queryOptions
  })
}

export const useLikesByContentIdAndSearch = (
  content_id,
  search,
  options = {}
) => {
  const { page = 1, limit = 20, ...queryOptions } = options
  return useQuery({
    queryKey: [
      'LIKES_BY_CONTENT_ID_AND_SEARCH',
      content_id,
      search,
      page,
      limit
    ],
    queryFn: async () => {
      if (!content_id) throw new Error('Content ID is required')
      const resp = await fetchLikesByContentIdAndSearch(
        content_id,
        search,
        page,
        limit
      )
      const data = resp?.data || resp
      return {
        ...resp,
        likes:
          data?.likes ||
          data?.report_reasons ||
          (Array.isArray(data) ? data : [])
      }
    },
    enabled: !!content_id,
    ...queryOptions
  })
}

export const useCommentsByContentId = (content_id, isUGC, options = {}) => {
  const { page = 1, limit = 20, ...queryOptions } = options
  return useQuery({
    queryKey: ['COMMENTS_BY_CONTENT_ID', content_id, page, limit],
    queryFn: async () => {
      if (!content_id) throw new Error('Content ID is required')
      const resp = await fetchCommentsByContentId(
        content_id,
        isUGC,
        page,
        limit
      )
      return resp?.comments ?? resp
    },
    enabled: !!content_id,
    ...queryOptions
  })
}

export const useInfiniteCommentsByContentId = (
  content_id,
  isUGC,
  options = {}
) => {
  const { limit = 20, ...queryOptions } = options
  return useInfiniteQuery({
    queryKey: ['INFINITE_COMMENTS_BY_CONTENT_ID', content_id, isUGC, limit],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      if (!content_id) throw new Error('Content ID is required')
      return await fetchCommentsByContentId(content_id, isUGC, pageParam, limit)
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next || undefined
    },
    enabled: !!content_id,
    ...queryOptions
  })
}

export const usePopularTools = (options = {}) => {
  // Enhanced caching for popular AI tools with placeholder data for instant feedback
  return useQuery({
    queryKey: ['POPULAR_TOOLS'],
    queryFn: async () => {
      return await fetchPopularTools()
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 20 * 60 * 1000, // 20 minutes
    ...options
  })
}

export const usePostComment = (options = {}) => {
  return useMutation({
    mutationKey: ['POST_COMMENT'],
    mutationFn: postCommentAPI,
    ...options
  })
}

export const usePickFromList = (params = {}, options = {}) => {
  const { content_type } = params
  // Enhanced caching for pick from list (AI tool context) with placeholder data
  return useQuery({
    queryKey: ['GET_PICK_FROM_LIST', content_type],
    queryFn: async () => {
      let resp
      if (content_type === CONTENT_TYPE.VIDEO)
        resp = await fetchVideoPickFromList()
      else resp = await fetchPickFromList()

      return resp
    },
    staleTime: 0, // fetch every time when enabled
    cacheTime: 20 * 60 * 1000, // 20 minutes
    placeholderData: Array(6).fill({ id: 'placeholder', name: 'Loading...', icon: '/placeholder-icon.svg' }),
    ...options
  })
}

export const useFetchDoMore = (tool_id, content_id, options = {}) => {
  const finalToolId = ensureAiToolId(tool_id) || tool_id
  // Enhanced caching for AI tool "Do More" results
  return useQuery({
    queryKey: ['DO_MORE_BY_ID', finalToolId, content_id],
    queryFn: async () => {
      return await fetchDoMore(finalToolId, content_id)
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 20 * 60 * 1000, // 20 minutes
    enabled: !!finalToolId && finalToolId !== 'reset-session' && !!content_id,
    ...options
  })
}

export const useVideoPickFromList = (options = {}) => {
  return useQuery({
    queryKey: ['GET_VIDEO_PICK_FROM_LIST'],
    queryFn: fetchVideoPickFromList,
    ...options
  })
}

export const useDiscoverData = (options = {}) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY.GET_DISCOVER_DATA],
    initialPageParam: 0,

    queryFn: async ({ pageParam }) => {
      return await fetchDiscoverData(10, pageParam)
    },

    getNextPageParam: (lastPage) => {
      const postsTray = lastPage?.data?.trays?.find(
        (tray) => tray.trayType === 'posts'
      )

      const next = postsTray?.next

      if (
        next === null ||
        next === undefined ||
        next === '' ||
        next === 'null' ||
        next === 'undefined' ||
        isNaN(Number(next))
      ) {
        return undefined
      }

      return Number(next)
    },

    staleTime:0,
    cacheTime:0,
    refetchPage: (_, index) => index === 0,
    ...options
  })
}

export const useShare = (options = {}) => {
  const { title = '', text = '', url = '', isUGC = '' } = options
  const queryClient = useQueryClient()
  const share = useNativeShare()
  return useMutation({
    mutationFn: (content_id) => fetchShareApi(content_id, isUGC),
    onSuccess: (response) => {
      queryClient.invalidateQueries(QUERY_KEY.GET_DISCOVER_DATA)
      share({
        title: title,
        text: text,
        url: response?.share_link || response?.content_file_url
      })
    },
    ...options
  })
}
export const useShareCount = () => {
  return useMutation({
    mutationFn: ({ content_id, isUGC }) =>
      fetchShareCount(content_id, isUGC),
  })
}